import { router, usePage } from "@inertiajs/react"
import { Building2, Check, ChevronsUpDown } from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import { cn } from "@/lib/utils"
import type { MyOrg, SharedProps } from "@/types"

// Groups the flat my_organizations list into partner blocks (a partner org
// followed by its customers, those whose parent_org_id === the partner.id)
// plus any standalone orgs that don't belong to a partner shown in the list.
function groupOrgs(orgs: MyOrg[]): {
  partners: { partner: MyOrg; customers: MyOrg[] }[]
  standalone: MyOrg[]
} {
  const partners = orgs.filter((o) => o.partner)
  const partnerIds = new Set(partners.map((p) => p.id))
  const claimed = new Set<number>()

  const grouped = partners.map((partner) => {
    const customers = orgs.filter((o) => o.parent_org_id === partner.id)
    customers.forEach((c) => claimed.add(c.id))
    return { partner, customers }
  })

  // Anything left over: not a partner and not a customer of a listed partner.
  const standalone = orgs.filter(
    (o) => !partnerIds.has(o.id) && !claimed.has(o.id),
  )

  return { partners: grouped, standalone }
}

function OrgRow({
  org,
  current,
  indented,
}: {
  org: MyOrg
  current: boolean
  indented?: boolean
}) {
  const routes = useOrgRoutes()
  return (
    <DropdownMenuItem
      onSelect={() => {
        if (!current) router.visit(routes.orgRootPath(org.slug))
      }}
      className={cn("gap-2", indented && "pl-6")}
    >
      {org.logo_url ? (
        <img
          src={org.logo_url}
          alt=""
          className="size-4 shrink-0 rounded-sm object-contain"
        />
      ) : (
        <Building2 className="size-4 shrink-0" />
      )}
      <span className="truncate">{org.name}</span>
      {current && <Check className="ml-auto size-4 shrink-0" />}
    </DropdownMenuItem>
  )
}

export function OrgSwitcher() {
  const { props } = usePage<SharedProps>()
  const { state } = useSidebar()
  const myOrgs = props.my_organizations
  const currentId = props.organization?.id

  if (!myOrgs || myOrgs.length === 0) return null

  const { partners, standalone } = groupOrgs(myOrgs)
  const currentName =
    myOrgs.find((o) => o.id === currentId)?.name ??
    props.organization?.name ??
    "Organization"
  const currentLogoUrl =
    props.organization?.logo_icon_url ?? props.organization?.logo_url ?? null

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent"
            >
              <Avatar className="size-8 rounded-md">
                {currentLogoUrl ? (
                  <AvatarImage
                    src={currentLogoUrl}
                    alt={`${currentName} logo`}
                    className="object-contain"
                  />
                ) : null}
                <AvatarFallback className="rounded-md bg-neutral-200 text-black dark:bg-neutral-700 dark:text-white">
                  <Building2 className="size-4" />
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{currentName}</span>
                <span className="text-muted-foreground truncate text-xs">
                  Switch organization
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4 shrink-0" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            align="start"
            side={state === "collapsed" ? "right" : "bottom"}
          >
            {partners.map(({ partner, customers }) => (
              <DropdownMenuGroup key={partner.id}>
                <DropdownMenuLabel className="text-muted-foreground text-xs">
                  {partner.name}
                </DropdownMenuLabel>
                <OrgRow org={partner} current={partner.id === currentId} />
                {customers.map((customer) => (
                  <OrgRow
                    key={customer.id}
                    org={customer}
                    current={customer.id === currentId}
                    indented
                  />
                ))}
              </DropdownMenuGroup>
            ))}
            {standalone.length > 0 && partners.length > 0 && (
              <DropdownMenuSeparator />
            )}
            {standalone.map((org) => (
              <OrgRow key={org.id} org={org} current={org.id === currentId} />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
